import {
  AccountDetail,
  PremiseInfo,
  Maybe,
  Scalars,
  AccountType,
  ReportOutageSource,
  OutageLookupResponse,
  ServicePointOutageDetails,
} from '../../../__generated__/pge-types';
import { isEmpty } from 'lodash';
import { useTranslation } from '../../../hooks/useTranslation';
import { useContext } from 'react';
import { NotificationsContext } from '../../../providers/NotificationsProvider';
import ROUTES from '../../../routes';
import { createStyles, makeStyles } from '@material-ui/core';

export type AccountDetailsList = {
  isPrimaryAccount: boolean;
  hasKnownOutage: boolean;
  description?: Maybe<Scalars['String']>;
  addressLine1?: Scalars['String'];
  accountNumber?: Scalars['String'];
  premiseInfos?: Maybe<PremiseInfo>[];
  encryptedPersonId?: Scalars['String'];
  mainCustomerName?: Scalars['String'];
};

const useStyles = makeStyles(() =>
  createStyles({
    alert: {
      margin: '5px 1em',
      '& > .MuiAlert-message > p': {
        margin: 0,
      },
    },
  }),
);

const useOutageCenterUtils = () => {
  const classes = useStyles();

  const getOutageAccountDetails = (accountDetails: AccountDetail[]) => {
    const accountDetailsList: AccountDetailsList[] = [];

    const commercialAccount = accountDetails.filter(account => {
      const hasPremiseInfo =
        account.premiseInfo?.length &&
        account.premiseInfo.some(p => p?.isEligibleForOutage);
      return (
        account.accountType !== AccountType.Res &&
        account.isActive === true &&
        hasPremiseInfo
      ); // if a account is not Residential then it is a commercial account
    });

    const residentialAccounts = accountDetails.filter(account => {
      const hasPremiseInfo =
        account.premiseInfo?.length &&
        account.premiseInfo.some(p => p?.isEligibleForOutage);
      return (
        account.accountType === AccountType.Res &&
        account.isActive === true &&
        hasPremiseInfo
      ); // if a account is not Residential then it is a commercial account
    });

    const primaryResAccount = residentialAccounts.filter(
      account => account.isDefault,
    );
    const primaryCommAccount = commercialAccount.filter(
      account => account.isDefault,
    );
    const allPrimaryAccounts = [...primaryResAccount, ...primaryCommAccount];

    if (allPrimaryAccounts?.length) {
      allPrimaryAccounts.forEach(account => {
        const {
          isDefault,
          premiseInfo,
          description,
          accountNumber,
          encryptedPersonId,
          mainCustomerName,
        } = account;

        const validPremiseInfos =
          premiseInfo &&
          premiseInfo.filter(
            currPremiseInfo =>
              currPremiseInfo?.isEligibleForOutage &&
              currPremiseInfo?.servicePointDetails &&
              currPremiseInfo.servicePointDetails.length > 0,
          );
        if (!!validPremiseInfos) {
          const details = getReportedOutageAccountInfo(
            validPremiseInfos,
            description,
            isDefault,
            accountNumber,
            encryptedPersonId,
            mainCustomerName,
          );
          accountDetailsList.push(details);
        }
      });
    }

    //If there were no primary Commercial Accounts but we have commercial accounts then display the first commercial account received from gql query.
    if (commercialAccount?.length && !primaryCommAccount?.length) {
      const {
        isDefault,
        premiseInfo,
        description,
        accountNumber,
        encryptedPersonId,
        mainCustomerName,
      } = commercialAccount[0]; // display first account if all accounts are commercial.

      const validPremiseInfos =
        premiseInfo &&
        premiseInfo.filter(
          currPremiseInfo =>
            currPremiseInfo?.isEligibleForOutage &&
            currPremiseInfo?.servicePointDetails &&
            currPremiseInfo.servicePointDetails.length > 0,
        );
      if (!!validPremiseInfos) {
        const details = getReportedOutageAccountInfo(
          premiseInfo,
          description,
          isDefault,
          accountNumber,
          encryptedPersonId,
          mainCustomerName,
        );
        accountDetailsList.push(details);
      }
    }

    // display all residentialAccounts
    if (residentialAccounts?.length) {
      residentialAccounts.forEach(account => {
        const {
          isDefault,
          premiseInfo,
          description,
          accountNumber,
          encryptedPersonId,
          mainCustomerName,
        } = account;
        if (!isDefault) {
          // because default/primary accounts are already displayed
          const validPremiseInfos =
            premiseInfo &&
            premiseInfo.filter(
              currPremiseInfo =>
                currPremiseInfo?.isEligibleForOutage &&
                currPremiseInfo?.servicePointDetails &&
                currPremiseInfo.servicePointDetails.length > 0,
            );
          if (!!validPremiseInfos) {
            const details = getReportedOutageAccountInfo(
              premiseInfo,
              description,
              isDefault,
              accountNumber,
              encryptedPersonId,
              mainCustomerName,
            );
            accountDetailsList.push(details);
          }
        }
      });
    }
    return accountDetailsList;
  };

  const getReportedOutageAccountInfo = (
    premiseInfos: Maybe<PremiseInfo>[],
    description: Maybe<Scalars['String']> | undefined,
    isDefault: boolean,
    accountNumber: Scalars['String'],
    encryptedPersonId: Scalars['String'],
    mainCustomerName: Scalars['String'],
  ) => {
    const outageReportedPremiseInfo = premiseInfos?.find(premiseInfo => {
      return premiseInfo?.servicePointDetails
        ? premiseInfo?.servicePointDetails?.filter(point => {
            return point?.outageInfo?.hasKnownOutage;
          }).length > 0
        : [];
    });

    return {
      isPrimaryAccount: isDefault,
      hasKnownOutage: !isEmpty(outageReportedPremiseInfo),
      description: description || accountNumber,
      accountNumber: accountNumber,
      premiseInfos,
      encryptedPersonId: encryptedPersonId,
      addressLine1: outageReportedPremiseInfo?.addressLine1
        ? outageReportedPremiseInfo.addressLine1
        : premiseInfos && premiseInfos[0]?.addressLine1,
      mainCustomerName: mainCustomerName,
    };
  };

  const getRequestSource = () => {
    if (
      typeof window !== 'undefined' &&
      window.location.pathname.replace(/\/$/, '') === ROUTES.OUTAGES_MOBILE_APP
    ) {
      return ReportOutageSource.Mob;
    } else {
      return ReportOutageSource.Web;
    }
  };

  const buildLeftPanelDataFromAccountPremise = (
    premise: PremiseInfo,
  ): OutageLookupResponse | undefined => {
    if (premise?.servicePointDetails) {
      const outages = premise.servicePointDetails.reduce(
        (acc: ServicePointOutageDetails[], outage) => {
          outage?.outageInfo &&
            acc.push({
              ...outage.outageInfo,
              encryptedServicePointId: outage.encryptedServicePointId,
            });
          return acc;
        },
        [],
      );
      if (Boolean(outages)) {
        return {
          isAccountExists: true,
          isEligibleToReport: true, // TODO: Confirm with Ishrat
          encryptedPremiseId: premise.encryptedPremiseId,
          houseNumber: `${premise.addressLine1},
          ${premise.city}, ${premise.state} ${premise.postal}`,
          outages,
        };
      }
    }
    return;
  };
  const { t } = useTranslation();
  const notificationContext = useContext(NotificationsContext);
  const setErrorNotification = (isError: boolean) => {
    notificationContext.setState({
      isOpen: isError,
      message: isError
        ? t('GENERIC_ERROR_NOTIFICATION_MESSAGE_BODY')
        : undefined,
      severity: isError ? 'error' : undefined,
      alertStyle: classes.alert,
    });
    isError && typeof window !== 'undefined' && window.scrollTo(0, 0);

    return { setErrorNotification };
  };

  return {
    getOutageAccountDetails,
    getReportedOutageAccountInfo,
    getRequestSource,
    buildLeftPanelDataFromAccountPremise,
    setErrorNotification,
  };
};

export default useOutageCenterUtils;
